import { useState, useEffect } from 'react';
import { auth, firestore } from '../config';
import { 
  collection, 
  query,
  where,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  FirestoreError,
  QueryDocumentSnapshot
} from 'firebase/firestore';

export interface Workspace {
  id: string;
  name: string;
  createdBy: string;
  createdAt: string;
}

export interface WorkspaceUser {
  id: string;
  email: string;
  role: 'admin' | 'member';
  joinedAt: string;
  workspaceId: string;
  userId: string;
}

export interface WorkspaceMembership {
  workspaceId: string;
  userId: string;
}

export const useWorkspace = () => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');

  useEffect(() => {
    if (!auth.currentUser) {
      setWorkspaces([]);
      setStatus('success');
      return;
    }

    const workspaceUsersRef = collection(firestore, 'workspaceUsers');
    const q = query(workspaceUsersRef, where('userId', '==', auth.currentUser.uid));

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        try {
          const workspaceIds = snapshot.docs.map((doc: QueryDocumentSnapshot) => {
            const data = doc.data() as WorkspaceMembership;
            return data.workspaceId;
          });
          
          if (workspaceIds.length === 0) {
            setWorkspaces([]);
            setStatus('success');
            return;
          }

          const workspaceDocs = await Promise.all(
            workspaceIds.map(id => 
              getDocs(query(collection(firestore, 'workspaces'), where('__name__', '==', id)))
            )
          );

          const workspacesData = workspaceDocs.flatMap(querySnapshot => 
            querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            } as Workspace))
          );

          setWorkspaces(workspacesData);
          setStatus('success');
        } catch (error) {
          console.error('Error fetching workspaces:', error);
          setStatus('error');
        }
      },
      (error: FirestoreError) => {
        console.error('Workspace users error:', error);
        setStatus('error');
      }
    );

    return () => unsubscribe();
  }, []);

  const createWorkspace = async (name: string): Promise<string> => {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to create workspace');
    }
    
    const workspaceRef = doc(collection(firestore, 'workspaces'));
    const workspace = {
      name,
      createdBy: auth.currentUser.uid,
      createdAt: new Date().toISOString()
    };
    
    await setDoc(workspaceRef, workspace);
    
    const membershipRef = doc(collection(firestore, 'workspaceUsers'));
    await setDoc(membershipRef, {
      userId: auth.currentUser.uid,
      email: auth.currentUser.email,
      workspaceId: workspaceRef.id,
      role: 'admin',
      joinedAt: new Date().toISOString()
    });
    
    return workspaceRef.id;
  };

  const addUserToWorkspace = async (workspaceId: string, email: string, role: 'admin' | 'member' = 'member'): Promise<void> => {
    const membershipRef = doc(collection(firestore, 'workspaceUsers'));
    await setDoc(membershipRef, {
      email,
      workspaceId,
      role,
      joinedAt: new Date().toISOString()
    });
  };

  const removeUserFromWorkspace = async (workspaceId: string, userId: string): Promise<void> => {
    const q = query(
      collection(firestore, 'workspaceUsers'),
      where('userId', '==', userId),
      where('workspaceId', '==', workspaceId)
    );
    const querySnapshot = await getDocs(q);
    
    const deletions = querySnapshot.docs.map(doc => 
      deleteDoc(doc.ref)
    );
    await Promise.all(deletions);
  };

  const updateWorkspace = async (workspaceId: string, data: Partial<Workspace>): Promise<void> => {
    const workspaceRef = doc(firestore, `workspaces/${workspaceId}`);
    await updateDoc(workspaceRef, data);
  };

  return {
    workspaces,
    status,
    createWorkspace,
    addUserToWorkspace,
    removeUserFromWorkspace,
    updateWorkspace
  };
};